/**
 * CabinVideoLessons — course_cabin_main_v1 kursunun 105 video dersini
 * modüle göre gruplu accordion listesiyle gösterir.
 *
 * Veri kaynağı: useCabinAllLessons hook
 *   → GET /api/courses/course_cabin_main_v1/modules  (lessons koleksiyonu, modül gruplu)
 *   → GET /api/enrollments  (completed_lessons ilerleme)
 *
 * UYARI: /api/cabin/modules → cabin_lessons (interaktif Duolingo, vimeo_url=NULL).
 * Bu sayfada KULLANILMAZ — tamamen ayrı bir ürün.
 *
 * İlk modül default expanded. Ders tıklaması → LessonPage (genel kurs video akışı)
 * route: /lesson/course_cabin_main_v1/{lesson_id}
 */

import { useState, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  PlayCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  BookOpen,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCabinAllLessons } from '../hooks/cabin/useCabinAllLessons';
import CabinErrorState from '../components/cabin/CabinErrorState';

// ── Skeleton ──────────────────────────────────────────────────────────────────
function SkeletonAccordion() {
  return (
    <div className="border-2 border-gray-100 rounded-2xl overflow-hidden bg-white animate-pulse">
      <div className="h-14 bg-gray-100" />
      <div className="px-4 py-3 flex flex-col gap-2">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-10 bg-gray-50 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

// ── Tek ders satırı ───────────────────────────────────────────────────────────
function LessonRow({ lesson, onNavigate }) {
  const { t, lang } = useLanguage();
  const isCompleted = lesson.status === 'completed';

  const title =
    lang === 'tr'
      ? (lesson.title_tr || lesson.title || '')
      : (lesson.title_en || lesson.title || '');

  return (
    <motion.button
      whileHover={{ x: 2 }}
      whileTap={{ scale: 0.99 }}
      onClick={() => onNavigate(lesson)}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px] hover:bg-sky-50 cursor-pointer"
      aria-label={`${title}${isCompleted ? ` — ${t('cabin.video_lessons.completed_label') || 'Tamamlandı'}` : ''}`}
      data-testid={`video-lesson-row-${lesson.lesson_id}`}
    >
      {/* Sıra no + ikon */}
      <div className="shrink-0 w-7 h-7 flex items-center justify-center">
        {isCompleted ? (
          <CheckCircle
            size={20}
            className="text-emerald-500"
            aria-hidden="true"
          />
        ) : (
          <PlayCircle
            size={20}
            className="text-[var(--brand-primary)] opacity-70"
            aria-hidden="true"
          />
        )}
      </div>

      {/* Sıra numarası */}
      <span className="shrink-0 text-xs font-bold text-[var(--text-muted)] w-5 text-right">
        {lesson.globalOrder || lesson.order || ''}
      </span>

      {/* Başlık */}
      <p className={`flex-1 text-sm font-semibold truncate ${
        isCompleted
          ? 'text-[var(--text-muted)] line-through'
          : 'text-[var(--text-main)]'
      }`}>
        {title}
      </p>

      {/* Süre */}
      {lesson.duration_minutes > 0 && (
        <span className="flex items-center gap-1 text-xs text-[var(--text-muted)] shrink-0">
          <Clock size={11} aria-hidden="true" />
          {lesson.duration_minutes}
          {' '}{t('lesson.minutes') || 'dk'}
        </span>
      )}
    </motion.button>
  );
}

// ── Modül accordion ───────────────────────────────────────────────────────────
function ModuleAccordion({ module: mod, index, defaultExpanded, onNavigate }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { t } = useLanguage();
  const shouldReduce = useReducedMotion();

  const lessons = mod.lessons || [];
  const completedCount = lessons.filter((l) => l.status === 'completed').length;
  const pct = lessons.length > 0 ? Math.round((completedCount / lessons.length) * 100) : 0;

  const accordionId = `video-lessons-mod-${mod.module_id || index}`;

  return (
    <div
      className="border-2 border-[var(--border-light)] rounded-2xl overflow-hidden bg-white shadow-[0_4px_0_0_var(--border-light)]"
      data-testid={`video-lessons-accordion-${mod.module_id || index}`}
    >
      {/* Header */}
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
        aria-expanded={expanded}
        aria-controls={accordionId}
        data-testid={`video-lessons-toggle-${mod.module_id || index}`}
      >
        <span className="text-xl shrink-0 leading-none" aria-hidden="true">
          {mod.emoji || '✈️'}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[var(--text-main)] truncate">
            {mod.title}
          </p>
          <p className="text-xs text-[var(--text-muted)]">
            {completedCount}/{lessons.length}{' '}
            {t('unit.lessons_done') || 'ders tamamlandı'}
          </p>
        </div>

        {/* Progress pill */}
        {lessons.length > 0 && (
          <div className="shrink-0 flex items-center gap-2">
            <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden" aria-hidden="true">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-[var(--text-muted)] w-7 text-right">
              {pct}%
            </span>
          </div>
        )}

        {expanded ? (
          <ChevronUp size={16} className="text-[var(--text-muted)] shrink-0" aria-hidden="true" />
        ) : (
          <ChevronDown size={16} className="text-[var(--text-muted)] shrink-0" aria-hidden="true" />
        )}
      </button>

      {/* Lesson listesi */}
      <motion.div
        id={accordionId}
        initial={false}
        animate={
          shouldReduce
            ? {}
            : { height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 }
        }
        style={shouldReduce ? { display: expanded ? 'block' : 'none' } : {}}
        transition={{ duration: 0.2 }}
        className="overflow-hidden"
      >
        <div className="px-2 pb-2 flex flex-col gap-0.5">
          {lessons.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] px-4 py-3">
              {t('unit.no_lessons') || 'Bu modülde henüz ders yok.'}
            </p>
          ) : (
            lessons.map((lesson) => (
              <LessonRow
                key={lesson.lesson_id}
                lesson={lesson}
                onNavigate={onNavigate}
              />
            ))
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ── Sayfa ─────────────────────────────────────────────────────────────────────
const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

export default function CabinVideoLessons() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const shouldReduce = useReducedMotion();
  const { data: modules, isLoading, isError, refetch } = useCabinAllLessons();

  const handleLessonNavigate = useCallback((lesson) => {
    // lessons koleksiyonundaki video dersler için genel LessonPage kullanılır.
    // CabinLessonPlayer cabin_lessons koleksiyonuna bağlıdır — bu dersler orada YOK.
    navigate(`/lesson/course_cabin_main_v1/${lesson.lesson_id}`);
  }, [navigate]);

  if (isError && !isLoading) {
    return (
      <CabinErrorState
        onRetry={refetch}
        backPath="/programs/kabin-memurlugu/modules"
        testId="video-lessons-error"
      />
    );
  }

  // Toplam ders sayısı
  const totalLessons = modules
    ? modules.reduce((sum, m) => sum + (m.lessons?.length || 0), 0)
    : 0;
  const completedTotal = modules
    ? modules.reduce(
        (sum, m) => sum + (m.lessons || []).filter((l) => l.status === 'completed').length,
        0
      )
    : 0;

  return (
    <motion.div
      variants={shouldReduce ? {} : pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="min-h-screen bg-[var(--bg-app)]"
      data-testid="cabin-video-lessons"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-700 px-4 pt-6 pb-10 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <PlayCircle size={28} className="text-white shrink-0" aria-hidden="true" />
          <div>
            <h1 className="text-2xl font-black text-white">
              {t('cabin.video_lessons.title') || 'Video Dersler'}
            </h1>
            <p className="text-sm text-sky-200">
              {t('cabin.video_lessons.subtitle') || 'Kabin Memurluğu Eğitimi'}
              {!isLoading && totalLessons > 0 && (
                <span className="ml-2 font-semibold">
                  · {completedTotal}/{totalLessons}{' '}
                  {t('cabin.video_lessons.done_label') || 'tamamlandı'}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 pb-12">
        {isLoading ? (
          <div className="flex flex-col gap-4">
            {[...Array(5)].map((_, i) => <SkeletonAccordion key={i} />)}
          </div>
        ) : !modules || modules.length === 0 ? (
          /* Empty state */
          <div
            className="bg-white rounded-2xl border-2 border-[var(--border-light)] p-10 text-center shadow-[0_4px_0_0_var(--border-light)]"
            data-testid="video-lessons-empty"
          >
            <BookOpen size={48} className="text-[var(--text-muted)] mx-auto mb-4" aria-hidden="true" />
            <p className="text-lg font-black text-[var(--text-main)] mb-2">
              {t('cabin.video_lessons.empty.title') || 'Henüz ders eklenmedi'}
            </p>
            <p className="text-sm text-[var(--text-muted)] mb-6">
              {t('cabin.video_lessons.empty.sub') || 'Dersler yakında yayına alınacak.'}
            </p>
            <button
              onClick={() => navigate('/programs/kabin-memurlugu/modules')}
              className="px-6 py-2.5 bg-[var(--brand-primary)] text-white font-bold rounded-xl hover:opacity-90 transition-opacity focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
            >
              {t('cabin.video_lessons.empty.cta') || 'Modüllere Git'}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {modules.map((mod, index) => (
              <ModuleAccordion
                key={mod.module_id || index}
                module={mod}
                index={index}
                defaultExpanded={index === 0}
                onNavigate={handleLessonNavigate}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
